import { apiSlice } from './apiSlice'
import { ENDPOINTS } from '../../lib/endpoints'

export interface PollVoter {
  id: string
  displayName: string
  avatarUrl: string | null
}

export interface PollOption {
  id: string
  text: string
  sortOrder: number
  count: number
  voters: PollVoter[]
}

export interface Poll {
  id: string
  discussionId: string
  question: string
  isAnonymous: boolean
  allowRevote: boolean
  expiresAt: string | null
  createdAt: string
  totalVotes: number
  options: PollOption[]
  userVotedOptionId: string | null
}

export interface CreatePollBody {
  question: string
  options: string[]
  isAnonymous: boolean
  allowRevote: boolean
  expiresAt?: string | null
}

export const pollsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({

    getPoll: builder.query<{ poll: Poll }, string>({
      query: (discussionId) => ENDPOINTS.polls.get(discussionId),
      providesTags: (_r, _e, discussionId) => [{ type: 'Poll' as const, id: discussionId }],
    }),

    createPoll: builder.mutation<{ poll: Poll }, { discussionId: string; body: CreatePollBody }>({
      query: ({ discussionId, body }) => ({
        url: ENDPOINTS.polls.create(discussionId),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { discussionId }) => [{ type: 'Poll' as const, id: discussionId }],
    }),

    votePoll: builder.mutation<{ poll: Poll }, { discussionId: string; optionId: string }>({
      query: ({ discussionId, optionId }) => ({
        url: ENDPOINTS.polls.vote(discussionId),
        method: 'POST',
        body: { optionId },
      }),
      // Optimistic update
      async onQueryStarted({ discussionId, optionId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          pollsApi.util.updateQueryData('getPoll', discussionId, (draft) => {
            const prev = draft.poll.userVotedOptionId
            if (prev === optionId) {
              // Unvote
              draft.poll.options = draft.poll.options.map(o =>
                o.id === optionId ? { ...o, count: Math.max(0, o.count - 1) } : o
              )
              draft.poll.userVotedOptionId = null
              draft.poll.totalVotes = Math.max(0, draft.poll.totalVotes - 1)
            } else {
              if (prev) {
                // Remove old vote
                draft.poll.options = draft.poll.options.map(o =>
                  o.id === prev ? { ...o, count: Math.max(0, o.count - 1) } : o
                )
              }
              draft.poll.options = draft.poll.options.map(o =>
                o.id === optionId ? { ...o, count: o.count + 1 } : o
              )
              draft.poll.userVotedOptionId = optionId
              if (!prev) draft.poll.totalVotes += 1
            }
          })
        )
        try { await queryFulfilled }
        catch { patch.undo() }
      },
      invalidatesTags: (_r, _e, { discussionId }) => [{ type: 'Poll' as const, id: discussionId }],
    }),

    deletePoll: builder.mutation<void, string>({
      query: (discussionId) => ({
        url: ENDPOINTS.polls.delete(discussionId),
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, discussionId) => [{ type: 'Poll' as const, id: discussionId }],
    }),

  }),
})

export const {
  useGetPollQuery,
  useCreatePollMutation,
  useVotePollMutation,
  useDeletePollMutation,
} = pollsApi
